import { useMemo, useState } from 'react';
import { Button } from '../ui/Button.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Input } from '../ui/Input.js';
import { Select } from '../ui/Select.js';
import { Textarea } from '../ui/Textarea.js';
import {
  initialCommissionForm,
  parseCommissionForm,
  type CommissionBasis,
  type CommissionFormValues,
  type CommissionTermsIntent,
  type CommissionTermsLike,
} from '../../lib/commission.js';

/**
 * The commission deal editor — SHARED by the college deal and a per-course
 * special deal, because the brief says a course override uses "the same form".
 * The two can be mounted at once, so every DOM id is namespaced by `idPrefix`
 * and the whole block is a named `role="group"`.
 *
 * State is one `useState` object rather than nine scalars: nine would mean nine
 * copies of the banner-clearing in nine onChange handlers. One object also means
 * ONE pure `parseCommissionForm` call, so the entire validation surface is a
 * single unit-tested function (lib/commission.ts) and this component only wires.
 */
export function CommissionTermsForm({
  idPrefix,
  groupLabel,
  current,
  defaultValidFrom,
  busy,
  submitLabel,
  onDirty,
  onRequestSave,
}: {
  idPrefix: string;
  groupLabel: string;
  current: CommissionTermsLike | null;
  /** The server's as-of date — never the browser's local today. */
  defaultValidFrom: string;
  busy: boolean;
  submitLabel: string;
  onDirty: () => void;
  onRequestSave: (body: CommissionTermsIntent) => void;
}) {
  const [values, setValues] = useState<CommissionFormValues>(() =>
    initialCommissionForm(current, defaultValidFrom),
  );
  const set = (patch: Partial<CommissionFormValues>) => {
    setValues((v) => ({ ...v, ...patch }));
    onDirty();
  };
  const parsed = useMemo(() => parseCommissionForm(values), [values]);
  const errors = parsed.ok ? {} : parsed.errors;
  const id = (field: string) => `${idPrefix}-${field}`;

  // Inline errors stay quiet while a field is untouched (the pricing console's
  // derived rule) — the "enter a rate or a flat amount" case is a permanent
  // muted hint plus a disabled Save, not a red error on a blank form.
  const showRateError = values.rate.trim() !== '' && errors.rate != null;
  const showFlatError = values.flat.trim() !== '' && errors.flat != null;
  const showMonthsError = values.basisMonths.trim() !== '' && errors.basisMonths != null;

  return (
    <div role="group" aria-label={groupLabel} className="mt-4 space-y-3">
      <div>
        <label htmlFor={id('rate')} className="mb-1 block text-sm text-ink">
          Rate (%)
        </label>
        <Input
          id={id('rate')}
          inputMode="decimal"
          placeholder="20"
          value={values.rate}
          onChange={(e) => set({ rate: e.target.value })}
          invalid={showRateError}
        />
        {showRateError && <p className="mt-1 text-xs text-danger">{errors.rate}</p>}
      </div>

      <div>
        <label htmlFor={id('flat')} className="mb-1 block text-sm text-ink">
          Flat amount per student (AUD)
        </label>
        <Input
          id={id('flat')}
          inputMode="decimal"
          placeholder="0.00"
          value={values.flat}
          onChange={(e) => set({ flat: e.target.value })}
          invalid={showFlatError}
        />
        {showFlatError && <p className="mt-1 text-xs text-danger">{errors.flat}</p>}
        <p className="mt-1 text-xs text-muted">Enter a rate, a flat amount, or both.</p>
      </div>

      <div>
        <label htmlFor={id('basis')} className="mb-1 block text-sm text-ink">
          Applies to
        </label>
        <Select
          id={id('basis')}
          value={values.basis}
          onChange={(e) =>
            // Clear the months whenever the basis moves: the API rejects a
            // basisMonths that arrives with any other basis.
            set({ basis: e.target.value as CommissionBasis, basisMonths: '' })
          }
        >
          <option value="whole_course">The whole course</option>
          <option value="first_year">The first year</option>
          <option value="first_6_months">The first 6 months</option>
          <option value="first_semester">The first semester</option>
          <option value="custom_months">A custom number of months</option>
        </Select>
      </div>

      {values.basis === 'custom_months' && (
        <div>
          <label htmlFor={id('months')} className="mb-1 block text-sm text-ink">
            Number of months
          </label>
          <Input
            id={id('months')}
            inputMode="numeric"
            placeholder="18"
            value={values.basisMonths}
            onChange={(e) => set({ basisMonths: e.target.value })}
            invalid={showMonthsError}
          />
          {showMonthsError && <p className="mt-1 text-xs text-danger">{errors.basisMonths}</p>}
        </div>
      )}

      <div>
        <label htmlFor={id('deduction')} className="mb-1 block text-sm text-ink">
          Calculated
        </label>
        <Select
          id={id('deduction')}
          value={values.deduction}
          onChange={(e) =>
            set({ deduction: e.target.value as CommissionFormValues['deduction'] })
          }
        >
          <option value="">Not agreed</option>
          <option value="pre">Before deductions</option>
          <option value="post">After deductions</option>
        </Select>
      </div>

      <div>
        <label htmlFor={id('route')} className="mb-1 block text-sm text-ink">
          Paid
        </label>
        <Select
          id={id('route')}
          value={values.paymentRoute}
          onChange={(e) =>
            set({ paymentRoute: e.target.value as CommissionFormValues['paymentRoute'] })
          }
        >
          <option value="">Not agreed</option>
          <option value="via_aussizz">Via Aussizz</option>
          <option value="direct">Direct to us</option>
        </Select>
      </div>

      <Checkbox
        id={id('visa')}
        label="500-visa students are always paid direct"
        checked={values.studentVisaForcesDirect}
        onChange={(e) => set({ studentVisaForcesDirect: e.target.checked })}
      />

      <div>
        <label htmlFor={id('notes')} className="mb-1 block text-sm text-ink">
          Notes
        </label>
        <Textarea
          id={id('notes')}
          rows={3}
          value={values.notes}
          onChange={(e) => set({ notes: e.target.value })}
          invalid={errors.notes != null}
        />
        {errors.notes && <p className="mt-1 text-xs text-danger">{errors.notes}</p>}
      </div>

      <div>
        <label htmlFor={id('valid-from')} className="mb-1 block text-sm text-ink">
          Effective from
        </label>
        {/* No `min`: the API permits any date and the brief asks for
            future-dating, so a stricter client rule would diverge. Defaults to
            the server's as-of date, never the browser's local today. */}
        <Input
          id={id('valid-from')}
          type="date"
          value={values.validFrom}
          onChange={(e) => set({ validFrom: e.target.value })}
          invalid={errors.validFrom != null}
        />
        {errors.validFrom && <p className="mt-1 text-xs text-danger">{errors.validFrom}</p>}
      </div>

      <Button
        disabled={!parsed.ok || busy}
        onClick={() => {
          if (parsed.ok) onRequestSave(parsed.body);
        }}
      >
        {submitLabel}
      </Button>
    </div>
  );
}
